import type { HandLandmark, HandSample } from '../domain/types'

// MediaPipe's left/right label flips easily when hands share a height, so the
// instrument never keys anything off it. Each detected hand is assigned to a
// persistent spatial slot (0 or 1) by proximity to where that slot was last
// seen; brand-new hands take free slots ordered right-to-left on screen.

export interface SlotMemory {
  position: HandLandmark | null
  lastSeenMs: number
}

export interface SlotAssignment {
  slots: [HandSample | null, HandSample | null]
  memory: [SlotMemory, SlotMemory]
}

const DUPLICATE_DISTANCE = 0.06
const REATTACH_DISTANCE = 0.38
const MEMORY_MS = 450

export function centroidOf(hand: HandSample): HandLandmark {
  const indices = [0, 5, 9, 13, 17].filter((index) => hand.landmarks[index])
  if (indices.length === 0) return { x: 0.5, y: 0.5, z: 0 }
  const sum = indices.reduce((acc, index) => ({ x: acc.x + hand.landmarks[index]!.x, y: acc.y + hand.landmarks[index]!.y, z: 0 }), { x: 0, y: 0, z: 0 })
  return { x: sum.x / indices.length, y: sum.y / indices.length, z: 0 }
}

export function emptyMemory(): [SlotMemory, SlotMemory] {
  return [{ position: null, lastSeenMs: Number.NEGATIVE_INFINITY }, { position: null, lastSeenMs: Number.NEGATIVE_INFINITY }]
}

export function assignSlots(hands: readonly HandSample[], memory: [SlotMemory, SlotMemory], timestampMs: number): SlotAssignment {
  const candidates = dedupe(hands).slice(0, 2)
  const centroids = candidates.map(centroidOf)
  const remembered = memory.map((slot) => slot.position && timestampMs - slot.lastSeenMs <= MEMORY_MS ? slot.position : null)

  let best: (number | null)[] = [null, null]
  let bestCost = Number.POSITIVE_INFINITY
  const orders: number[][] = candidates.length === 2 ? [[0, 1], [1, 0]] : candidates.length === 1 ? [[0], [1]] : [[]]
  for (const order of orders) {
    const assignment: (number | null)[] = [null, null]
    let cost = 0
    let valid = true
    if (candidates.length === 1) {
      const slotIndex = order[0]!
      const distance = remembered[slotIndex] ? measure(remembered[slotIndex]!, centroids[0]!) : null
      if (distance !== null && distance > REATTACH_DISTANCE) valid = false
      const otherIndex = 1 - slotIndex
      // Prefer a remembered slot; when neither remembers, a lone hand takes slot 0.
      cost = distance ?? (remembered[otherIndex] ? 1 : slotIndex === 0 ? 0.5 : 0.75)
      assignment[slotIndex] = 0
    } else {
      order.forEach((handIndex, slotIndex) => {
        const distance = remembered[slotIndex] ? measure(remembered[slotIndex]!, centroids[handIndex]!) : null
        if (distance !== null && distance > REATTACH_DISTANCE) valid = false
        cost += distance ?? 0.5
        assignment[slotIndex] = handIndex
      })
      // With no memory at all, slot 0 is the hand further right on screen.
      if (candidates.length === 2 && !remembered[0] && !remembered[1]) cost += centroids[assignment[0]!]!.x < centroids[assignment[1]!]!.x ? 0.1 : 0
    }
    if (valid && cost < bestCost) { bestCost = cost; best = assignment }
  }
  if (bestCost === Number.POSITIVE_INFINITY) {
    // Every remembered slot is too far away: treat the hands as new arrivals.
    const sorted = candidates.map((_, index) => ({ index, x: centroids[index]!.x })).sort((a, b) => b.x - a.x)
    best = [sorted[0]?.index ?? null, sorted[1]?.index ?? null]
  }

  const slots: [HandSample | null, HandSample | null] = [null, null]
  const nextMemory = emptyMemory()
  for (let slotIndex = 0; slotIndex < 2; slotIndex += 1) {
    const handIndex = best[slotIndex]
    if (handIndex === null || handIndex === undefined) {
      nextMemory[slotIndex] = memory[slotIndex]!
      continue
    }
    const hand = candidates[handIndex]!
    slots[slotIndex] = { ...hand, slot: slotIndex }
    nextMemory[slotIndex] = { position: centroids[handIndex]!, lastSeenMs: timestampMs }
  }
  return { slots, memory: nextMemory as [SlotMemory, SlotMemory] }
}

// The landmarker occasionally reports one physical hand twice; keep the more confident copy.
function dedupe(hands: readonly HandSample[]): HandSample[] {
  const kept: HandSample[] = []
  for (const hand of [...hands].sort((a, b) => b.confidence - a.confidence)) {
    if (hand.landmarks.length < 21) continue
    const centroid = centroidOf(hand)
    if (kept.some((other) => measure(centroidOf(other), centroid) < DUPLICATE_DISTANCE)) continue
    kept.push(hand)
  }
  return kept
}

function measure(a: HandLandmark, b: HandLandmark): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}
